import { toast } from 'react-toastify';
import { useFieldArray, useForm } from 'react-hook-form';

import { licensesSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEditUserMutation } from '@common/cache';
import { LICENSE_TYPE, paths } from '@common/types';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useContext, useEffect, useState } from 'react';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';
import { InferType } from 'yup';

type ILicensesFormShape = InferType<typeof licensesSchema>;

const initialValues: ILicensesFormShape = {
  licenses: [],
};

export const useLicenses = (
  user: paths[`/user/{user_id}`]['get']['responses']['200']['content']['application/json'] & {
    id: string;
  },
) => {
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const { mutateAsync, isPending } = useEditUserMutation(user.id);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isSubmitting, isDirty, errors, isValid },
  } = useForm<ILicensesFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ILicensesFormShape, any, any>(licensesSchema),
    defaultValues: {
      ...initialValues,
      licenses: user.licenses,
    },
  });

  const {
    fields: licenseFields,
    append,
    remove,
  } = useFieldArray({ control, name: 'licenses' });

  const handleAddLicense = () => {
    append({ id: '', type: '' });
  };

  const handleRemoveLicense = (index: number) => {
    remove(index);
  };

  const submitHandler = async (values: ILicensesFormShape) => {
    try {
      await mutateAsync({
        user_id: user.id,
        licenses: values.licenses?.map(license => ({
          id: license.id,
          type: license.type as LICENSE_TYPE,
        })),
      });

      toast.success('Changes saved');
      reset(values);
      setIsFormClean(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    onSubmit();
    reset(getValues());
    setIsFormClean(true);
  };

  useEffect(() => {
    if (triggerClose && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange]);

  return {
    control,
    onSubmit,
    errors,
    isSubmitEnabled:
      isDirty && Object.keys(errors).length === 0 && !isSubmitting,
    isSubmitting,
    licenseFields,
    handleAddLicense,
    handleRemoveLicense,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
